import { useState } from "react";
import { Link } from "react-router-dom";
import {
  useRegisterMutation,
  useResendVerificationEmailMutation,
} from "../../features/api/authApiSlice";
import FuelLoading from "../../pages/FuelLoading"; // Reuses your beautiful road rolling car animation
import "./auth.scss";

export default function Register() {
  const [register, { isLoading }] = useRegisterMutation();
  const [resendVerificationEmail, { isLoading: isResending }] =
    useResendVerificationEmailMutation();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [success, setSuccess] = useState(false);

  // Custom Validation Error States (Matches donation/contact style rules)
  const [error, setError] = useState("");
  const [resendMessage, setResendMessage] = useState("");
  const [formErrors, setFormErrors] = useState({
    name: false,
    email: false,
    password: false,
  });

  // Instantly clears validation red rings the moment a user changes typing focus
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (error) setError("");
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const newErrors = { name: false, email: false, password: false };
    let hasError = false;

    // 1. Name validation check
    if (!formData.name.trim()) {
      setError("Please tell us your name to create an account.");
      newErrors.name = true;
      hasError = true;
    }

    // 2. Email verification check
    if (!formData.email.trim()) {
      setError("Please fill out your email address.");
      newErrors.email = true;
      hasError = true;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        setError(
          "Please format your email address accurately (driver@roadtrip.com)."
        );
        newErrors.email = true;
        hasError = true;
      }
    }

    // 3. Password safety constraint validation check
    if (!formData.password) {
      setError("Please choose a secure account password.");
      newErrors.password = true;
      hasError = true;
    } else if (formData.password.length < 6) {
      setError(
        "Password length parameters must be at least 6 characters long."
      );
      newErrors.password = true;
      hasError = true;
    }

    if (hasError) {
      setFormErrors(newErrors);
      return;
    }

    try {
      await register({
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
      }).unwrap();

      setRegisteredEmail(formData.email.trim().toLowerCase());
      setSuccess(true);
      setFormData({ name: "", email: "", password: "" });
    } catch (err) {
      setError(
        err?.data?.message || "Registration transmission failed down the line."
      );
    }
  };

  const handleResendVerification = async () => {
    setError("");
    setResendMessage("");
    try {
      const res = await resendVerificationEmail(registeredEmail).unwrap();
      setResendMessage(
        res?.message || "Verification link dispatched again successfully."
      );
    } catch (err) {
      setError(err?.data?.message || "Failed to resend validation data links.");
    }
  };

  // SUCCESS COMPONENT LAYER CARD: Displays custom inbox verification alert dashboard
  if (success) {
    return (
      <main className="auth-page">
        <section className="auth-card auth-card--center fuel-fade-in">
          <div className="auth-card__success-icon">📬 ✨ 🚗</div>
          <h1>Check Your Inbox</h1>
          <p className="auth-card__sub">
            We have broadcast a verification link to your device:
          </p>
          <div className="registered-email-badge">{registeredEmail}</div>

          <p className="auth-success-message">
            Please review your Inbox, Spam, Junk, Promotions, or Updates folder
            to activate your road tracking privileges.
          </p>

          <div className="auth-resend-zone">
            <p>Didn't receive your route link coordinates?</p>
            <button
              type="button"
              className="fuel-secondary-btn resend-btn"
              onClick={handleResendVerification}
              disabled={isResending}
            >
              {isResending
                ? "Dispatching Link..."
                : "🔄 Resend Verification Email"}
            </button>
            {resendMessage && (
              <p className="auth-inline-success">{resendMessage}</p>
            )}
          </div>

          <Link to="/login" className="fuel-primary-btn goto-login-btn">
            Go To Login
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="auth-page">
      {isLoading ? (
        <div className="auth-loading-box">
          <FuelLoading text="Mapping driver data logs and generating security tags..." />
        </div>
      ) : (
        <form
          className="auth-card fuel-fade-in"
          onSubmit={handleSubmit}
          noValidate
        >
          <div className="auth-card__title">
            <h1>Create Account</h1>
            <p>Join Mini Miles Adventures.</p>
          </div>

          <div className="auth-form-group">
            {/* Name input layout block */}
            <div className="auth-input-wrapper">
              <label htmlFor="reg-name">
                Name <span className="required-star">*</span>
              </label>
              <input
                id="reg-name"
                name="name"
                type="text"
                className={formErrors.name ? "input-error" : ""}
                value={formData.name}
                onChange={handleChange}
                placeholder="Adventurer Name"
              />
            </div>

            {/* Email input layout block */}
            <div className="auth-input-wrapper">
              <label htmlFor="reg-email">
                Email Address <span className="required-star">*</span>
              </label>
              <input
                id="reg-email"
                name="email"
                type="email"
                className={formErrors.email ? "input-error" : ""}
                value={formData.email}
                onChange={handleChange}
                placeholder="driver@roadtrip.com"
              />
            </div>

            {/* Password input layout block */}
            <div className="auth-input-wrapper">
              <label htmlFor="reg-pass">
                Password <span className="required-star">*</span>
              </label>
              <input
                id="reg-pass"
                name="password"
                type="password"
                className={formErrors.password ? "input-error" : ""}
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
              />
            </div>
          </div>

          {/* Master Form Global Alert Banner */}
          {error && <div className="auth-error-banner">{error}</div>}

          <button
            type="submit"
            className="fuel-primary-btn register-submit-btn"
          >
            Create Driver Profile 🚀
          </button>

          <footer className="auth-card__footer">
            <span>
              Already have an account?{" "}
              <Link to="/login" className="footer-auth-link">
                Login
              </Link>
            </span>
          </footer>
        </form>
      )}
    </main>
  );
}
